import type { ComputedRef } from 'vue'
import type { RouteLocationRaw } from '#vue-router'
import type { Meta } from '@brighterly/lib-core-types/api/interfaces'
import type { AppBadgeColor } from '@shared/ui/badges'
import type { Promocode } from './interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useSidebarPrivileges } from '@entities/viewer'
import { useDayjs } from '@shared/lib/format'
import { AppRoute } from '@shared/config'
import { DEFAULT_APP_TABLE_META } from '@shared/ui/tables'
import { ROWS_PER_PAGE_OPTIONS } from '@shared/ui/paginations'

import { PROMOCODE_STATUS_COLORS, usePromocodeOptions } from './promocode-options'
import { usePromocodesApi } from '../api/list.hook'
import { usePromocodesStore } from './store'

interface TablePromocodesReturn {
  promocodes: ComputedRef<Promocode[]>
  isLoading: ComputedRef<boolean>
  hasPromocodes: ComputedRef<boolean>
  canManage: ComputedRef<boolean>
  meta: ComputedRef<Meta>
  perPageOptions: ComputedRef<number[]>
  formatDateTime: (value: string | null) => string
  getExpiresLabel: (promocode: Promocode) => string
  getStatusColor: (status: string) => AppBadgeColor
  getStatusText: (status: string) => string
  getOrdersLink: (promocode: Promocode) => RouteLocationRaw
  askEdit: (promocode: Promocode) => void
  setChangePage: (value: number) => Promise<void>
  setChangePerPage: (value: number) => Promise<void>
}

export const useTablePromocodes = (): TablePromocodesReturn => {
  const router = useRouter()
  const route = useRoute()
  const store = usePromocodesStore()
  const dayjs = useDayjs()
  const { getStatusLabel } = usePromocodeOptions()
  const { canManagePromocodes } = useSidebarPrivileges()

  const promocodes = computed<Promocode[]>(() => store.getPromocodes)
  const isLoading = computed<boolean>(() => store.isLoading)
  const hasPromocodes = computed<boolean>(() => store.hasPromocodes)
  const canManage = computed<boolean>(() => canManagePromocodes.value)
  const meta = computed<Meta>(() => store.getMeta ?? DEFAULT_APP_TABLE_META)

  const perPageOptions = computed<number[]>(() => {
    const current = meta.value.per_page
    if (!current || ROWS_PER_PAGE_OPTIONS.includes(current)) return ROWS_PER_PAGE_OPTIONS

    return [...ROWS_PER_PAGE_OPTIONS, current].sort((a, b) => a - b)
  })

  const formatDateTime = (value: string | null): string =>
    value ? dayjs(value).format(DateFormat.KanbanDate) : '—'

  const getExpiresLabel = (promocode: Promocode): string =>
    promocode.expires_at ? dayjs(promocode.expires_at).format(DateFormat.KanbanDate) : 'Eternal'

  const getStatusColor = (status: string): AppBadgeColor => PROMOCODE_STATUS_COLORS[status] ?? 'slate'
  const getStatusText = (status: string): string => getStatusLabel(status)

  const getOrdersLink = (promocode: Promocode): RouteLocationRaw => ({
    path: AppRoute.Orders,
    query: { promocode_id: String(promocode.id) },
  })

  const askEdit = (promocode: Promocode): void => {
    store.setPromocodeToEdit(promocode)
  }

  const setChangePage = async (value: number): Promise<void> => {
    store.setCurrentPage(value)
    await router.push({
      query: {
        ...route.query,
        page: String(value),
      },
    })
    await usePromocodesApi()
  }

  const setChangePerPage = async (value: number): Promise<void> => {
    store.setPerPage(value)
    store.setCurrentPage(1)
    await router.push({
      query: {
        ...route.query,
        per_page: String(value),
        page: undefined,
      },
    })
    await usePromocodesApi()
  }

  return {
    promocodes,
    isLoading,
    hasPromocodes,
    canManage,
    meta,
    perPageOptions,
    formatDateTime,
    getExpiresLabel,
    getStatusColor,
    getStatusText,
    getOrdersLink,
    askEdit,
    setChangePage,
    setChangePerPage,
  }
}
